import { useState, useMemo } from 'react';
import { Modal, Input, Select, InputNumber, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import {
  PageHeader, StatsRow, StatCard, FilterBar, TableWrap, TableFooter,
  Pill, Btn, avatarSlot, Avatar,
} from '../../components/ui';
import { toast } from '../../lib/toast';

type Tab = 'all' | 'active' | 'inactive';

interface TeamRow {
  id: number;
  name: string;
  managerId: number;
  managerName: string;
  description: string | null;
  isActive: boolean;
  memberCount: number;
  customerCount: number;
  monthlyTarget: number;
  monthRevenue: number;
  createdAt: string;
}

interface OrgTreeData {
  teams: Array<TeamRow & { members: any[] }>;
  unassigned: any[];
}

/** 组织架构树（公司 → 团队 → 成员） — 可折叠 */
function OrgTree({ data }: { data?: OrgTreeData }) {
  const [open, setOpen] = useState(true);
  if (!data) return null;
  const totalMembers = data.teams.reduce((s, t) => s + t.members.length, 0) + data.unassigned.length;
  return (
    <div className="org-tree-card">
      <div className="org-tree-head" onClick={() => setOpen(!open)}>
        <span style={{ fontWeight: 500 }}>组织架构</span>
        <span style={{ color: 'var(--text-3)', fontSize: 12, marginLeft: 8 }}>
          {data.teams.length} 个团队 · {totalMembers} 个账号
        </span>
        <span style={{ marginLeft: 'auto', color: 'var(--text-3)', fontSize: 12 }}>
          {open ? '收起 ▴' : '展开 ▾'}
        </span>
      </div>
      {open && (
        <div className="org-tree-body">
          <div className="org-tree-root">
            <span className="org-tree-co-icon">🏢</span>
            <span style={{ fontWeight: 500 }}>欢科 · 公司</span>
          </div>
          {data.teams.map((t) => (
            <div key={t.id} className="org-tree-team">
              <div className="org-tree-team-head">
                <span className="org-tree-team-name">{t.name}</span>
                <span className="org-tree-team-meta">
                  主管：{t.managerName} · {t.members.length} 人 · {t.customerCount} 客户
                </span>
              </div>
              <div className="org-tree-members">
                {t.members.length === 0 && (
                  <div className="org-tree-empty">暂无成员</div>
                )}
                {t.members.map((m) => {
                  const isMgr = Number(m.id) === Number(t.managerId);
                  const cur = Number(m.currentCustomerCount ?? 0);
                  const cap = Number(m.customerCapacity ?? 0);
                  return (
                    <div key={m.id} className="org-tree-member">
                      <Avatar userId={m.id} src={(m as any).avatarUrl} name={m.realName} size="sm" />
                      <span style={{ fontSize: 13 }}>{m.realName}</span>
                      {isMgr && <Pill tone="warning">主管</Pill>}
                      {!isMgr && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                        {m.role === 'sales' ? '销售员' : m.role === 'manager' ? '交付主管' : m.role}
                      </span>}
                      {cap > 0 && (
                        <span style={{ fontSize: 11, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
                          持客 {cur}/{cap}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {data.unassigned.length > 0 && (
            <div className="org-tree-team">
              <div className="org-tree-team-head">
                <span className="org-tree-team-name">未分配团队</span>
                <span className="org-tree-team-meta">{data.unassigned.length} 个账号（admin/director 不属于某具体团队）</span>
              </div>
              <div className="org-tree-members">
                {data.unassigned.map((m) => (
                  <div key={m.id} className="org-tree-member">
                    <Avatar userId={m.id} src={(m as any).avatarUrl} name={m.realName} size="sm" />
                    <span style={{ fontSize: 13 }}>{m.realName}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{m.role}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface CreateTeamForm {
  name: string;
  managerId?: number;
  description?: string;
  monthlyTarget?: number;
  memberIds?: number[];
}

function CreateTeamModal({ open, onClose, onSubmitted }: {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [form, setForm] = useState<CreateTeamForm>({ name: '' });
  // 拉所有 manager/director 候选
  const { data: candidates } = useQuery({
    queryKey: ['team-mgr-candidates'],
    queryFn: async () => {
      const [mgrs, dirs] = await Promise.all([
        $.get<any>('/users', { role: 'manager', pageSize: 100 }),
        $.get<any>('/users', { role: 'director', pageSize: 100 }),
      ]);
      return [...(mgrs.items ?? []), ...(dirs.items ?? [])];
    },
    enabled: open,
  });
  // 拉无团队的 sales 候选作初始成员
  const { data: salesPool } = useQuery({
    queryKey: ['team-sales-pool'],
    queryFn: () => $.get<any>('/users', { role: 'sales', pageSize: 100 }),
    enabled: open,
  });
  const orphanSales = (salesPool?.items ?? []).filter((s: any) => !s.teamId);

  const create = useMutation({
    mutationFn: (payload: CreateTeamForm) => $.post('/teams', payload),
    onSuccess: () => {
      toast.success(`团队 "${form.name}" 已创建`);
      setForm({ name: '' });
      onSubmitted();
      onClose();
    },
    // 错误 toast 由 axios interceptor 统一展示
  });

  const canSubmit = !!form.name && !!form.managerId;
  return (
    <Modal
      open={open}
      title="新建团队"
      onCancel={onClose}
      onOk={() => create.mutate(form)}
      okButtonProps={{ disabled: !canSubmit, loading: create.isPending }}
      okText="创建"
      cancelText="取消"
      width={480}
    >
      <div className="form-grid" style={{ gridTemplateColumns: '1fr', gap: 14 }}>
        <div className="form-field">
          <label className="form-label">团队名 *</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="如：北京一队" />
        </div>
        <div className="form-field">
          <label className="form-label">主管 *</label>
          <Select
            placeholder={candidates?.length ? '从 manager/director 角色中选' : '先创建主管账号'}
            value={form.managerId}
            onChange={(v) => setForm({ ...form, managerId: v })}
            options={(candidates ?? []).map((u: any) => ({
              value: Number(u.id),
              label: `${u.realName} (${u.role === 'manager' ? '主管' : '总监'} · ${u.phone})`,
            }))}
            style={{ width: '100%' }}
          />
        </div>
        <div className="form-field">
          <label className="form-label">描述</label>
          <Input.TextArea value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
        </div>
        <div className="form-field">
          <label className="form-label">月度目标金额（元）</label>
          <InputNumber
            value={form.monthlyTarget}
            onChange={(v) => setForm({ ...form, monthlyTarget: Number(v ?? 0) })}
            min={0}
            step={10000}
            style={{ width: '100%' }}
            formatter={(v) => `¥ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(v) => Number((v ?? '').replace(/[¥,\s]/g, '')) as any}
          />
        </div>
        <div className="form-field">
          <label className="form-label">初始成员（无团队的销售员）</label>
          <Select
            mode="multiple"
            placeholder={orphanSales.length ? '可多选' : '暂无可分配的销售员'}
            value={form.memberIds ?? []}
            onChange={(v) => setForm({ ...form, memberIds: v })}
            options={orphanSales.map((u: any) => ({
              value: Number(u.id),
              label: `${u.realName} · ${u.phone}`,
            }))}
            style={{ width: '100%' }}
          />
        </div>
      </div>
    </Modal>
  );
}

function EditTeamModal({ team, onClose, onSubmitted }: {
  team: TeamRow | null;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [form, setForm] = useState<any>({});
  const open = !!team;
  // 重置表单当 team 变化
  useMemo(() => {
    if (team) setForm({
      name: team.name,
      managerId: Number(team.managerId),
      description: team.description ?? '',
      monthlyTarget: Number(team.monthlyTarget ?? 0),
      isActive: team.isActive,
    });
  }, [team]);
  const { data: candidates } = useQuery({
    queryKey: ['team-mgr-candidates'],
    queryFn: async () => {
      const [mgrs, dirs] = await Promise.all([
        $.get<any>('/users', { role: 'manager', pageSize: 100 }),
        $.get<any>('/users', { role: 'director', pageSize: 100 }),
      ]);
      return [...(mgrs.items ?? []), ...(dirs.items ?? [])];
    },
    enabled: open,
  });
  const update = useMutation({
    mutationFn: (payload: any) => $.patch(`/teams/${team!.id}`, payload),
    onSuccess: () => {
      toast.success('已更新');
      onSubmitted();
      onClose();
    },
    // 错误 toast 由 axios interceptor 统一展示
  });
  if (!team) return null;
  return (
    <Modal
      open={open}
      title={`编辑团队 · ${team.name}`}
      onCancel={onClose}
      onOk={() => update.mutate(form)}
      okButtonProps={{ loading: update.isPending }}
      okText="保存"
      cancelText="取消"
      width={480}
    >
      <div className="form-grid" style={{ gridTemplateColumns: '1fr', gap: 14 }}>
        <div className="form-field">
          <label className="form-label">团队名</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="form-field">
          <label className="form-label">主管</label>
          <Select
            value={form.managerId}
            onChange={(v) => setForm({ ...form, managerId: v })}
            options={(candidates ?? []).map((u: any) => ({
              value: Number(u.id),
              label: `${u.realName} (${u.role === 'manager' ? '主管' : '总监'} · ${u.phone})`,
            }))}
            style={{ width: '100%' }}
          />
          <div style={{ fontSize: 11, color: 'var(--warning-text)', marginTop: 4 }}>
            ⚠ 换主管不会改变客户归属，只切换团队的统计/审批负责人
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">描述</label>
          <Input.TextArea value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
        </div>
        <div className="form-field">
          <label className="form-label">月度目标</label>
          <InputNumber value={form.monthlyTarget} onChange={(v) => setForm({ ...form, monthlyTarget: Number(v ?? 0) })} min={0} step={10000} style={{ width: '100%' }} />
        </div>
      </div>
    </Modal>
  );
}

/** 添加成员 Modal — 从无团队 sales 多选加入 */
function AddMembersModal({ team, onClose, onSubmitted }: {
  team: TeamRow | null;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [selected, setSelected] = useState<number[]>([]);
  const open = !!team;
  // 拉无团队的 sales 候选
  const { data: salesPool } = useQuery({
    queryKey: ['team-add-sales-pool'],
    queryFn: () => $.get<any>('/users', { role: 'sales', pageSize: 100 }),
    enabled: open,
  });
  const orphanSales = (salesPool?.items ?? []).filter((s: any) => !s.teamId);

  const addOne = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: number; userId: number }) =>
      $.post(`/teams/${teamId}/members`, { userId }),
  });

  const handleSubmit = async () => {
    if (!team || selected.length === 0) return;
    // 批量串行（保证错误可追溯到具体用户）
    for (const uid of selected) {
      try { await addOne.mutateAsync({ teamId: team.id, userId: uid }); }
      catch (e: any) { message.error(`#${uid}: ${e?.message ?? '加入失败'}`); }
    }
    toast.success(`已加入 ${selected.length} 人到 ${team.name}`);
    setSelected([]);
    onSubmitted();
    onClose();
  };

  if (!team) return null;
  return (
    <Modal
      open={open}
      title={`添加成员到 · ${team.name}`}
      onCancel={() => { setSelected([]); onClose(); }}
      onOk={handleSubmit}
      okButtonProps={{ disabled: selected.length === 0, loading: addOne.isPending }}
      okText={selected.length > 0 ? `添加 ${selected.length} 人` : '添加'}
      cancelText="取消"
      width={520}
    >
      <div style={{ marginBottom: 8, color: 'var(--text-2)', fontSize: 13 }}>
        {orphanSales.length > 0 ? `${orphanSales.length} 个未分配团队的销售员可加入` : '暂无可分配的销售员（所有销售员已有团队）'}
      </div>
      <Select
        mode="multiple"
        placeholder="选择要加入的销售员"
        value={selected}
        onChange={setSelected}
        options={orphanSales.map((u: any) => ({
          value: Number(u.id),
          label: `${u.realName} · ${u.phone}${u.isProbation ? ' · 试用期' : ''}`,
        }))}
        style={{ width: '100%' }}
        disabled={orphanSales.length === 0}
      />
      <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-3)' }}>
        加入后销售员的直属上级自动设为团队主管 · {team.managerName}
      </div>
    </Modal>
  );
}

export function AdminTeamsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<TeamRow | null>(null);
  const [addingMembersTo, setAddingMembersTo] = useState<TeamRow | null>(null);
  const pageSize = 20;

  const { data: teams, isLoading } = useQuery({
    queryKey: ['admin-teams'],
    queryFn: () => $.get<TeamRow[]>('/teams'),
  });
  const { data: tree } = useQuery({
    queryKey: ['admin-org-tree'],
    queryFn: () => $.get<OrgTreeData>('/teams/org-tree'),
  });

  const filtered = useMemo(() => {
    const all = teams ?? [];
    if (tab === 'active') return all.filter((t) => t.isActive);
    if (tab === 'inactive') return all.filter((t) => !t.isActive);
    return all;
  }, [teams, tab]);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const totalMembers = (teams ?? []).reduce((s, t) => s + Number(t.memberCount ?? 0), 0);
  const totalCustomers = (teams ?? []).reduce((s, t) => s + Number(t.customerCount ?? 0), 0);
  const totalManagers = new Set((teams ?? []).map((t) => String(t.managerId))).size;

  const remove = useMutation({
    mutationFn: (id: number) => $.delete(`/teams/${id}`),
    onSuccess: () => {
      toast.success('团队已停用');
      qc.invalidateQueries({ queryKey: ['admin-teams'] });
      qc.invalidateQueries({ queryKey: ['admin-org-tree'] });
    },
    // 错误 toast 由 axios interceptor 统一展示
  });

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ['admin-teams'] });
    qc.invalidateQueries({ queryKey: ['admin-org-tree'] });
  };

  return (
    <>
      <PageHeader
        breadcrumb={['系统', '团队管理']}
        title="团队管理"
        subtitle="组织架构 · 主管 · 月度目标"
        actions={<Btn kind="primary" onClick={() => setCreateOpen(true)}>+ 新建团队</Btn>}
      />
      <div className="work-content">
        <OrgTree data={tree} />

        <StatsRow>
          <StatCard label="团队总数" value={teams?.length ?? 0} unit="个" />
          <StatCard label="主管数" value={totalManagers} unit="人" />
          <StatCard label="总成员数" value={totalMembers} unit="人" valueColor="success" />
          <StatCard label="总客户数" value={totalCustomers} unit="个" />
        </StatsRow>

        <FilterBar
          active={tab}
          onChange={(k) => { setTab(k as Tab); setPage(1); }}
          tabs={[
            { key: 'all', label: '全部', count: teams?.length ?? 0 },
            { key: 'active', label: '在建', count: (teams ?? []).filter((t) => t.isActive).length },
            { key: 'inactive', label: '已停用', count: (teams ?? []).filter((t) => !t.isActive).length },
          ]}
        />

        <TableWrap>
          <table className="ck-table">
            <thead>
              <tr>
                <th style={{ width: '15%' }}>团队名</th>
                <th style={{ width: '11%' }}>主管</th>
                <th style={{ width: '8%' }}>成员数</th>
                <th style={{ width: '10%' }}>客户总数</th>
                <th style={{ width: '12%' }}>月度目标</th>
                <th style={{ width: '16%' }}>完成率</th>
                <th style={{ width: '8%' }}>状态</th>
                <th style={{ width: '20%', textAlign: 'right' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>加载中…</td></tr>
              )}
              {!isLoading && paged.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>暂无团队 · 点右上角"+ 新建团队"</td></tr>
              )}
              {paged.map((t) => {
                const slot = avatarSlot(t.id);
                // P2-1 完成率：本月营收 / 月目标
                const target = Number(t.monthlyTarget ?? 0);
                const rev = Number(t.monthRevenue ?? 0);
                const pctRaw = target > 0 ? (rev / target) * 100 : 0;
                const pct = Math.round(pctRaw);
                const exceeded = pctRaw > 100;
                const barWidth = Math.min(100, pctRaw);
                const barColor = !target
                  ? 'var(--text-3)'
                  : exceeded
                    ? 'var(--warning)' // 金色（用 warning 橙偏金）
                    : pctRaw >= 70
                      ? 'var(--success)'
                      : 'var(--text-3)';
                return (
                  <tr key={t.id}>
                    <td>
                      <div className="cust-cell">
                        <div className={`cust-avatar c${slot}`}>{t.name.charAt(0)}</div>
                        <div className="cust-info">
                          <div className="cust-name">{t.name}</div>
                          <div className="cust-meta">{t.description ?? '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className="time-text">{t.managerName}</span></td>
                    <td><span className="time-text">{t.memberCount}</span></td>
                    <td><span className="time-text">{t.customerCount}</span></td>
                    <td>
                      <span className="money">
                        {target > 0 ? `¥${target.toLocaleString()}` : '—'}
                      </span>
                    </td>
                    <td>
                      {target === 0 ? (
                        <span className="time-text muted">未设目标</span>
                      ) : (
                        <div className="tbl-progress">
                          <div className="tbl-progress-bar">
                            <div className="tbl-progress-fill" style={{ width: `${barWidth}%`, background: barColor }} />
                          </div>
                          <span className="tbl-progress-val" style={exceeded ? { color: 'var(--warning-text)', fontWeight: 500 } : undefined}>
                            {exceeded ? `已超额 ${pct - 100}%` : `${pct}%`}
                          </span>
                        </div>
                      )}
                    </td>
                    <td>{t.isActive ? <Pill tone="success">在建</Pill> : <Pill tone="default">已停用</Pill>}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row-actions">
                        <button className="row-action" onClick={() => setEditing(t)} data-tip="编辑团队">编辑</button>
                        <button className="row-action" onClick={() => setAddingMembersTo(t)} data-tip="批量加入未分配销售员">添加成员</button>
                        <button
                          className="row-action danger"
                          onClick={() => {
                            Modal.confirm({
                              title: `确认停用团队 "${t.name}"？`,
                              content: '成员不为空时需先转移成员到其他团队',
                              okText: '确认停用',
                              okType: 'danger',
                              cancelText: '取消',
                              onOk: () => remove.mutateAsync(t.id),
                            });
                          }}
                          data-tip="软删除团队"
                        >停用</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > pageSize && (
            <TableFooter total={filtered.length} page={page} pageSize={pageSize} onPage={setPage} />
          )}
        </TableWrap>
      </div>

      <CreateTeamModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmitted={refetchAll}
      />
      <EditTeamModal
        team={editing}
        onClose={() => setEditing(null)}
        onSubmitted={refetchAll}
      />
      <AddMembersModal
        team={addingMembersTo}
        onClose={() => setAddingMembersTo(null)}
        onSubmitted={refetchAll}
      />
    </>
  );
}
